import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, first, map, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { FeatureFlagApiService } from '../api/feature-flag.service';
import { FeatureFlagKeys, FeatureFlags } from '../models/FeatureFlags';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private _featureFlags$ = new BehaviorSubject<FeatureFlags>(null);
  private _loading$ = new BehaviorSubject<boolean>(false);

  constructor(private featureFlagApiService: FeatureFlagApiService) {}

  /**
   * Ensures feature flags are loaded from API if not already cached.
   * Returns cached flags immediately if available.
   * Handles deduplication for multiple simultaneous calls.
   * @returns Observable<FeatureFlags> - Loaded or cached feature flags
   */
  ensureLoaded(): Observable<FeatureFlags> {
    // If already loaded, return cached value immediately
    if (this._featureFlags$.value !== null) {
      return of(this._featureFlags$.value);
    }

    // If currently loading, wait for existing load to complete
    if (this._loading$.value) {
      return this._featureFlags$.pipe(
        filter((flags) => flags !== null),
        first()
      );
    }

    // Load from API
    this._loading$.next(true);
    return this.featureFlagApiService.getFeatureFlags().pipe(
      map(({ response }) => response),
      tap((flags) => {
        this.updateFeatureFlags(flags);
        this._loading$.next(false);
      }),
      catchError((error) => {
        console.error('Failed to load feature flags:', error);
        this._loading$.next(false);
        // Return empty flags on error to allow app to continue
        const emptyFlags = {} as FeatureFlags;
        this.updateFeatureFlags(emptyFlags);
        return of(emptyFlags);
      })
    );
  }

  getFeatureFlags(): BehaviorSubject<FeatureFlags> {
    return this._featureFlags$;
  }

  updateFeatureFlags(flags: FeatureFlags): void {
    this._featureFlags$.next(flags);
  }

  /**
   * Checks if a specific feature flag is enabled.
   * Returns a long-lived Observable that emits the current value and future updates.
   * Automatically triggers loading if flags haven't been loaded yet.
   * @param key - The feature flag key to check
   * @returns Observable<boolean> - Observable that emits flag status
   */
  hasFeatureFlag(key: FeatureFlagKeys): Observable<boolean> {
    // Trigger loading if not already loaded or loading
    if (this._featureFlags$.value === null && !this._loading$.value) {
      this.ensureLoaded().subscribe(); // Trigger load but don't wait
    }

    // Return observable from BehaviorSubject for components using withLatestFrom, async pipe, etc.
    return this._featureFlags$.pipe(map((flags) => flags?.[key as string]?.enabled ?? false));
  }
}
